import { Box, styled } from "@mui/material";
import React from "react";

import { CopyButton } from "@components";

import { isEmptyValue } from "@helpers/utils";

import TruncatedTextWithTooltip from "../components/TruncatedTextWithTooltip";
import { COPY_ICON_SPACING } from "../helpers/constants";
import type { FieldRenderer } from "./types";

const StyledDefaultNode = styled("div")({
  "&:hover + .copyTextButton": {
    opacity: 1,
  },
});

const StyledCopyButton = styled(CopyButton)({
  position: "absolute",
  right: `-${COPY_ICON_SPACING}`,
  top: "50%",
  transform: "translateY(-50%)",
});

type FieldRendererWithDefinedNode = (
  fieldValue: unknown,
  tooltipValue?: unknown,
  onCopySuccess?: () => void,
) => ReturnType<FieldRenderer> & { node: React.ReactElement };

const renderer: FieldRendererWithDefinedNode = (
  fieldValue: unknown,
  tooltipValue,
  onCopySuccess?: () => void,
) => {
  if (isEmptyValue(fieldValue)) {
    return {
      formattedText: "",
      node: <div>-</div>,
    };
  }

  const ContainerWithHoverState = () => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ position: "relative" }}
      >
        <StyledDefaultNode>
          <TruncatedTextWithTooltip
            text={formattedText}
            tooltipText={tooltipText}
            placement="bottom-start"
          />
        </StyledDefaultNode>
        <Box
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          className="copyTextButton"
        >
          <StyledCopyButton
            text={copyText || ""}
            size="small"
            visibleOnHover={true}
            isHovered={isHovered}
            tooltipText="Copy to clipboard"
            tabIndex={-1}
            onCopySuccess={onCopySuccess}
          />
        </Box>
      </div>
    );
  };

  const formattedText = String(fieldValue);
  const tooltipText = tooltipValue ? String(tooltipValue) : "";
  const copyText = tooltipText || formattedText;

  return {
    formattedText,
    node: <ContainerWithHoverState />,
  };
};

export default renderer;
